/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type { UploadFile } from 'ember-file-upload';
import type { IEvidenceFileSlot } from 'uplisting-frontend/components/ui/protection/dispute-panel/response';
import type { DisputeEvidenceFileField } from 'uplisting-frontend/models/dispute';

interface IArgs {
  slots: IEvidenceFileSlot[];
  busyField?: DisputeEvidenceFileField;
  downloadingField?: DisputeEvidenceFileField;

  onAttach(key: DisputeEvidenceFileField, file: UploadFile): void;
  onRemove(key: DisputeEvidenceFileField): void;
  onDownload(key: DisputeEvidenceFileField): void;
}

interface UiProtectionDisputePanelResponseFilesSignature {
  Element: HTMLDivElement;

  Args: IArgs;
}

export default class UiProtectionDisputePanelResponseFilesComponent extends Component<UiProtectionDisputePanelResponseFilesSignature> {
  @tracked isModalOpen = false;
  @tracked selectedKey?: DisputeEvidenceFileField;

  // Only what the host has attached is listed. A document still uploading
  // holds its place, so the row does not appear out of nowhere afterwards.
  @cached
  get attachedSlots(): IEvidenceFileSlot[] {
    const { slots, busyField } = this.args;

    return slots.filter((slot) => slot.isAttached || slot.key === busyField);
  }

  @cached
  get availableKeys(): DisputeEvidenceFileField[] {
    const { slots, busyField } = this.args;

    return slots
      .filter((slot) => !slot.isAttached && slot.key !== busyField)
      .map((slot) => slot.key);
  }

  @cached
  get hasTypesLeft(): boolean {
    return this.availableKeys.length > 0;
  }

  @action
  handleOpenModal(): void {
    this.isModalOpen = true;
  }

  @action
  handleCloseModal(): void {
    Object.assign(this, { isModalOpen: false, selectedKey: undefined });
  }

  @action
  handleSelectKey(key: DisputeEvidenceFileField): void {
    this.selectedKey = key;
  }

  @action
  handleFileAdded(file: UploadFile): void {
    const key = this.selectedKey;

    if (!key) {
      return;
    }

    this.handleCloseModal();

    this.args.onAttach(key, file);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Protection::DisputePanel::Response::Files': typeof UiProtectionDisputePanelResponseFilesComponent;
    'ui/protection/dispute-panel/response/files': typeof UiProtectionDisputePanelResponseFilesComponent;
  }
}
